import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { alert } from '../utils/toast';
import type { MFASetupResponse } from '../types';
import { QrCode, Copy, Check, Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';

export const MFASetup: React.FC = () => {
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState('');
  const [copied, setCopied] = useState(false);

  const { data: mfaSetup, isLoading } = useQuery({
    queryKey: ['mfa-setup'],
    queryFn: async () => {
      const response = await api.post('/auth/mfa/setup');
      const payload = response.data?.data || response.data;
      return {
        ...payload,
        qr_code: payload?.qr_code || payload?.qrCode,
      } as MFASetupResponse;
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await api.post('/auth/mfa/verify-setup', { code, token: code });
      return response.data?.data || response.data;
    },
    onSuccess: () => {
      void alert.success('Concluído!', 'Configuração do MFA concluída com sucesso!');
      navigate('/login');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    verifyMutation.mutate(verificationCode);
  };

  const handleCopySecret = () => {
    if (mfaSetup?.secret) {
      void navigator.clipboard.writeText(mfaSetup.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary-500 mx-auto" />
          <p className="mt-4 text-slate-400 font-medium text-sm">
            A gerar chaves de segurança MFA...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Ambient background glows */}
      <div className="absolute top-1/4 -left-20 w-96 h-96 bg-primary-600/20 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-indigo-600/20 rounded-full blur-[128px] pointer-events-none" />

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-primary-600 to-indigo-500 mx-auto flex items-center justify-center shadow-lg shadow-primary-500/20 mb-6">
            <QrCode className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-extrabold text-white tracking-tight">
            Configurar Autenticação MFA
          </h2>
          <p className="mt-2 text-sm text-slate-400">
            Digitalize o código QR com a sua aplicação autenticadora (Google Authenticator, Authy,
            1Password)
          </p>
        </div>

        <div className="bg-slate-900/90 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 sm:p-10 shadow-2xl">
          {mfaSetup && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className="p-3 bg-white rounded-2xl shadow-lg border border-slate-200">
                  <img
                    src={mfaSetup.qr_code}
                    alt="MFA QR Code"
                    className="w-56 h-56 rounded-lg block"
                  />
                </div>
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 text-center">
                  Ou introduza a chave secreta manualmente:
                </p>
                <div className="flex items-center gap-2 bg-slate-800/80 border border-slate-700 p-2.5 rounded-xl">
                  <code className="text-xs font-mono font-bold text-slate-200 flex-1 text-center select-all break-all">
                    {mfaSetup.secret}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopySecret}
                    className="p-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors shrink-0"
                    title="Copiar Segredo"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 pt-2 border-t border-slate-800">
                {verifyMutation.isError && (
                  <div className="rounded-xl bg-rose-950/40 border border-rose-900/60 p-4">
                    <p className="text-sm font-medium text-rose-300">
                      {getErrorMessage(verifyMutation.error)}
                    </p>
                  </div>
                )}

                <div>
                  <label
                    htmlFor="code"
                    className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2 text-center"
                  >
                    Código de Confirmação (6 dígitos)
                  </label>
                  <input
                    id="code"
                    name="code"
                    type="text"
                    required
                    className="w-full py-3 px-4 rounded-xl border border-slate-700 bg-slate-800/80 text-white placeholder-slate-600 text-center text-2xl tracking-[0.3em] font-mono font-bold focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    maxLength={6}
                  />
                </div>

                <button
                  type="submit"
                  disabled={verifyMutation.isPending || verificationCode.length !== 6}
                  className="btn-primary w-full py-3 text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-primary-500/20 disabled:opacity-50 transition-all"
                >
                  {verifyMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> A validar código...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4" /> Concluir e Ativar MFA
                    </>
                  )}
                </button>

                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao Início de Sessão
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
